import moment from "moment";
import * as Yup from "yup";
import { callStockItemSearchByInternalId } from "../services/stockItemService";
import { isString } from "../services/helpersMethodsService";

/**
 * Utilitários de Movimentação de Estoque — port de helpers do
 * lovable-material-hub/components/estoque/MovtoMatTab.tsx.
 */

/** Linha de material em branco (estado local do formulário). */
export const emptyMaterialLine = () => ({
  stockItemId: "",
  variantId: "",
  newVariant: false,
  batch: "",
  newBatch: "",
  quantity: "",
  cost: "",
  manufacturerId: "",
  batchNumber: "",
  serialNumber: "",
  specification: "",
  measurementUnitId: "",
  expiration: "",
  expirationDays: "",
  expirationMode: "DATE",
  locations: [emptyLocation()],
  // Dados originais da NF (para conversão de unidade)
  nfProduct: "",
  nfQuantity: "",
  nfUnit: "",
  nfUnitCost: "",
  conversionFactor: "",
});

/** Localização física em branco. */
export const emptyLocation = () => ({
  warehouse: "",
  shelf: "",
  rack: "",
  drawer: "",
  deposit: "",
  tank: "",
});

/**
 * Extrai o peso (kg) de uma descrição de produto, ex.: "Premix Campo - 30kg" → 30.
 * Usado para sugerir o fator de conversão NF → Estoque.
 */
export const extractWeightKg = (description) => {
  if (!description) return null;
  const match = description.match(/(\d+(?:[.,]\d+)?)\s*kg/i);
  if (match) return parseFloat(match[1].replace(",", "."));
  return null;
};

/**
 * Monta as linhas de material a partir dos itens retornados pelo parser de NF.
 * Mantém os valores originais (qtd/unidade/custo) para conversão posterior.
 */
export const buildLinesFromNfItems = async (groupId, farmId, items) => {
  if (!Array.isArray(items) || items.length === 0) {
    return [emptyMaterialLine()];
  }
  const lines = [];
  for (const item of items) {
    const line = emptyMaterialLine();
    if (item.xProd) {
      // Tenta buscar o item de estoque pelo cProd da NF. Se houver exatamente 1 resultado, preenche stockItemId e measurementUnitId.
      try {
        const {
          data: { results: itens },
        } = await callStockItemSearchByInternalId({
          groupId,
          farmId,
          search: item.xProd,
        });
        if (itens && itens.length === 1) {
          const stockItem = itens[0];
          line.stockItemId = stockItem.id;
          line.measurementUnitId = stockItem.measurementUnitId;
          line.measurementUnit = stockItem.measurementUnitName;
        }
      } catch (error) {}
    }

    const nfQuantity =
      item.qCom != null
        ? isString(item.qCom)
          ? Number(item.qCom)
          : item.qCom
        : "";
    const nfUnitCost =
      item.vUnCom != null
        ? isString(item.vUnCom)
          ? Number(item.vUnCom)
          : item.vUnCom
        : "";
    lines.push({
      ...line,
      nfCode: item.cProd || "",
      nfProduct: item.xProd || "",
      nfQuantity,
      nfUnit: item.uCom || "",
      nfUnitCost,
      // Pré-preenche com os valores brutos da NF (1:1) até o usuário converter.
      quantity: nfQuantity,
      cost: nfUnitCost,
    });
  }
  return lines;
};

/**
 * Aplica o fator de conversão NF → Estoque numa linha.
 * quantity = nfQuantity × fator ; cost = nfUnitCost ÷ fator.
 */
export const applyConversionFactor = (line, factor) => {
  const f = factor || 0;
  const nfQty = line.nfQuantity || 0;
  const nfCost = line.nfUnitCost || 0;
  if (f <= 0) return { ...line, conversionFactor: factor };
  return {
    ...line,
    conversionFactor: factor,
    quantity: nfQty * f,
    cost: nfCost > 0 ? nfCost / f : line.cost,
  };
};

/**
 * Calcula a data de expiração quando o modo é "DAYS":
 * receiptDate + N dias. Retorna string "YYYY-MM-DD" ou "".
 */
export const computeExpirationByDays = (receiptDate, days) => {
  if (!receiptDate || !days) return "";
  const n = parseInt(days, 10);
  if (Number.isNaN(n)) return "";
  return moment(receiptDate).add(n, "days").format("YYYY-MM-DD");
};

/** Verdadeiro se o bloco de documento fiscal deve aparecer. */
export const shouldShowFiscalDocument = (type, origin) =>
  (type === "ENTRY" && origin === "PURCHASE") ||
  (type === "EXIT" && (origin === "SALE" || origin === "TRANSFER"));

/** Verdadeiro se o campo Solicitante é exigido (saída/retirada). */
export const requiresRequester = (type, origin) =>
  type === "EXIT" && origin === "WITHDRAWAL";

/** Verdadeiro se o movimento gerará pendência de auditoria (perda/ajuste). */
export const requiresAuditApproval = (type, origin) =>
  type === "EXIT" && (origin === "LOSS" || origin === "ADJUSTMENT");

/** Verdadeiro se o movimento exige Data de Recebimento e Fornecedor (Compra/Venda). */
export const requiresSupplierFields = (type, origin) =>
  (type === "ENTRY" && origin === "PURCHASE") ||
  (type === "EXIT" && origin === "SALE");

const isPresent = (value) =>
  value !== "" && value !== null && value !== undefined;

/** Schema Yup dos campos obrigatórios de uma linha de material. */
const buildMaterialLineSchema = (movementType) =>
  Yup.object().shape({
    stockItemId: Yup.mixed().test(
      "required",
      "Selecione o item de estoque",
      isPresent,
    ),
    measurementUnitId: Yup.mixed().test(
      "required",
      "Selecione a unidade de medida",
      isPresent,
    ),
    conversionFactor: Yup.mixed().when("nfQuantity", {
      is: (nfQuantity) => Boolean(nfQuantity),
      then: Yup.mixed().test(
        "required",
        "Informe o fator de conversão",
        isPresent,
      ),
    }),
    quantity: Yup.mixed().test("required", "Informe a quantidade", isPresent),
    cost:
      movementType === "ENTRY"
        ? Yup.mixed().test("required", "Informe o custo unitário", isPresent)
        : Yup.mixed(),
  });

/**
 * Valida os campos obrigatórios de uma linha de material via Yup.
 * Retorna um objeto onde cada chave é `true` quando o campo está inválido.
 */
export const validateMaterialLine = (line, movementType) => {
  try {
    buildMaterialLineSchema(movementType).validateSync(line, {
      abortEarly: false,
    });
    return {};
  } catch (err) {
    const errors = {};
    (err.inner || []).forEach((e) => {
      if (e.path) errors[e.path] = true;
    });
    return errors;
  }
};

/** Verdadeiro se a linha possui algum campo obrigatório inválido. */
export const hasMaterialLineErrors = (line, movementType) =>
  Object.keys(validateMaterialLine(line, movementType)).length > 0;
